/**
 * useChat — Redux-backed control hook for the single global Chat overlay.
 *
 * The actual <ChatPanel> is rendered exactly once, in PlatformHeader.tsx
 * (the app shell). Any other component (Speed Dial, Meeting Master, etc.)
 * that wants to open chat calls `openChat()` from this hook instead of
 * mounting its own <ChatPanel> instance — this prevents the overlay from
 * getting buried behind other Dialogs/Drawers and keeps open/close state
 * consistent app-wide.
 */
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../app/store';
import { openChat, closeChat, type ChatFilterContext } from '../app/redux/chat/chatSlice';

export const useChat = () => {
    const dispatch = useDispatch();
    const { isOpen, filterContext } = useSelector((state: RootState) => state.chat);

    return {
        isChatOpen: isOpen,
        chatFilterContext: filterContext,
        openChat: (context?: ChatFilterContext) => dispatch(openChat(context)),
        closeChat: () => dispatch(closeChat()),
    };
};
